// Copyright 2025, University of Maryland, All Rights Reserved
import React, { createContext, useContext, ReactNode } from "react";
import { useCASAuth } from "./useCASAuth";
import { CASConfig, CASUser } from "../types";
import { RefreshWarningDialog } from "../components/RefreshWarningDialog";

interface CASAuthContextValue {
  user: CASUser | null;
  isLoading: boolean;
  error: string | null;
  logout: () => void;
  getToken: () => string | null;
  addAuthHeader: (config: Record<string, unknown>) => Record<string, unknown>;
  showRefreshWarning: boolean;
  refreshCountdown: number;
  minutesUntilExpiry: number;
  onRefreshNow: () => void;
  onCancelRefresh: () => void;
}

const CASAuthContext = createContext<CASAuthContextValue | undefined>(
  undefined
);

interface CASAuthProviderProps {
  config: CASConfig;
  children: ReactNode;
  /**
   * Custom loading component to display while authenticating
   */
  loadingComponent?: ReactNode;
  /**
   * Custom error component to display when authentication fails
   */
  errorComponent?: (error: string) => ReactNode;
  /**
   * Callback fired when access is granted (user authenticated successfully)
   */
  onAccessGranted?: (authenticated: boolean, user: CASUser) => void;
  /**
   * Callback fired when access is denied (authentication failed)
   */
  onAccessDenied?: () => void;
}

/**
 * CAS Authentication Provider Component
 *
 * Wraps your app to provide CAS authentication context
 *
 * @example
 * ```tsx
 * <CASAuthProvider
 *   config={{
 *     casLoginUrl: "https://login.umd.edu/cas/login",
 *     serviceUrl: "http://localtest.dev.umd.edu:8080",
 *     apiGatewayUrl: "https://api.example.com/auth/cas/login"
 *   }}
 * >
 *   <App />
 * </CASAuthProvider>
 * ```
 */
export function CASAuthProvider({
  config,
  children,
  loadingComponent,
  errorComponent,
  onAccessGranted,
  onAccessDenied,
}: CASAuthProviderProps) {
  const auth = useCASAuth(config);

  // Call callbacks when auth state changes
  React.useEffect(() => {
    if (!auth.isLoading) {
      if (auth.user && onAccessGranted) {
        onAccessGranted(true, auth.user);
      } else if (auth.error && onAccessDenied) {
        onAccessDenied();
      }
    }
  }, [auth.isLoading, auth.user, auth.error, onAccessGranted, onAccessDenied]);

  // Show loading state
  if (auth.isLoading) {
    return (
      <>
        {loadingComponent || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="text-lg">Authenticating...</div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show error state
  if (auth.error) {
    return (
      <>
        {errorComponent ? (
          errorComponent(auth.error)
        ) : (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="text-lg text-red-600">
                Authentication Error: {auth.error}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Render the warning dialog component
  const WarningComponent = config.refreshWarningComponent || RefreshWarningDialog;

  return (
    <CASAuthContext.Provider value={auth}>
      {children}
      {auth.showRefreshWarning && (
        <WarningComponent
          minutesUntilExpiry={auth.minutesUntilExpiry}
          secondsRemaining={auth.refreshCountdown}
          onRefreshNow={auth.onRefreshNow}
          onCancel={auth.onCancelRefresh}
        />
      )}
    </CASAuthContext.Provider>
  );
}

/**
 * Hook to access CAS authentication context
 *
 * Must be used within a CASAuthProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, logout } = useCASAuthContext();
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}</p>
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCASAuthContext(): CASAuthContextValue {
  const context = useContext(CASAuthContext);

  if (context === undefined) {
    throw new Error("useCASAuthContext must be used within a CASAuthProvider");
  }

  return context;
}